import { createAction } from '@reduxjs/toolkit';

import type { AppState, BuyTypes } from '@modules';
import api from '@modules/api';
import { FiatCurrencyEnum } from '@modules/global/types';
import { createAsyncActions, createActions } from '@modules/helpers';

import { getFingerPrint3DS, getFingerPrintAntifraud } from '@selectors';

import { GETBuyRateResponse } from './types';

const actions = createActions('buy', {
  setCvv: name => createAction<string>(name),

  removeBuyToken: name => createAction<void>(name),

  resetBuyStatus: name => createAction<void>(name),

  resetBuyNext: name => createAction<void>(name),

  setSubtotal: name => createAction<GETBuyRateResponse['subtotal']>(name),
});

export const asyncActions = createAsyncActions('buy', {
  buyRequest: (data: BuyTypes.BuyRequestParams, thunkApi) => {
    const { global, user } = thunkApi.getState() as AppState;
    const sessionId = user.data?.session_id;
    const { fingerPrintData } = global;
    const redirectUrl = `${CONST.card3dsDomain}?${CONST.card3DS.buyReturnParam}={invoice_id}`;

    return api.post<BuyTypes.BuyRequestResponse>(thunkApi, '/buy', {
      ...data,
      ...getFingerPrint3DS(fingerPrintData),
      ...getFingerPrintAntifraud(fingerPrintData),
      client: 'web',
      session_id: sessionId,
      redirect_url: redirectUrl,
      device_session_id: window.deviceSessionId,
    });
  },

  buyRecheck: (data: BuyTypes.BuyRecheckParams, thunkApi) =>
    api.post<BuyTypes.BuyRecheckResponse>(thunkApi, '/buy/recheck', data),

  getBuyStatus: (id: string, thunkApi) =>
    api.get<BuyTypes.GETBuyIdStatusResponse>(thunkApi, `/buy/${id}/status`),

  getBuyStatusPolling: (id: string, thunkApi) =>
    api.get<BuyTypes.GETBuyIdStatusResponse>(thunkApi, `/buy/${id}/status`),

  getBuyVerificationDescriptor: (data: BuyTypes.POSTBuyIdStatusDescriptorParams, thunkApi) => {
    const { id, code } = data;
    const url = `buy/${id}/status/verify-descriptor`;
    return api.post<BuyTypes.POSTBuyIdStatusDescriptorResponse>(thunkApi, url, { code });
  },

  getBuyRate: (data: Omit<BuyTypes.GETBuyRateParams, 'is_total'>, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const { fiat } = state.global.currencies;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const is_total = fiat.includes(data.from as FiatCurrencyEnum);
    const params = { ...data, is_total };
    return api.get<BuyTypes.GETBuyRateResponse>(thunkApi, '/buy/rate', { params });
  },

  getRapydBuyRate: (data: BuyTypes.GetRapydBuyRateParams, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const { fiat } = state.global.currencies;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const isTotal = fiat.includes(data.from as FiatCurrencyEnum);
    const params = { ...data, isTotal };
    const url = '/wallet/buy/rate';
    return api.getGo<BuyTypes.GetRapydBuyRateResponse>(thunkApi, url, { params });
  },

  buyRapyd: (data: BuyTypes.BuyRapydParams, thunkApi) => {
    const url = '/wallet/invoice/create';
    return api.postGo<BuyTypes.BuyRapydResponse>(thunkApi, url, data);
  },

  getRapydBuyStatus: (id: string, thunkApi) => {
    const url = '/wallet/buy/status';
    return api.getGo<BuyTypes.GetRapydBuyStatusResponse>(thunkApi, url, { params: { tx_id: id } });
  },
});

export default { ...actions, ...asyncActions };
